import { createSelector } from 'reselect';

// Selectors
import {
  getTrim,
  getTransmission,
  getDriveType,
  getEngineType,
  getEngineSize
} from 'store/reducers/appointment/selectors';
import { getTrimsSelectedPosition } from 'store/reducers/userInteractions/selectors';
import { getDefaultDrivingConditions as getMakeDrivingConditions } from 'store/reducers/dealerSettings/selectors';

const propsSelector = (state, props) => props;
export const getTrimsData = state => state.trims;

export const validateTrim = createSelector(
  getTrimsData,
  propsSelector,
  ({ transmissions }, trimData) => {
    const compare = (field, value) =>
      field.value.toUpperCase() === value.toUpperCase();
    if (transmissions && trimData) {
      try {
        const transmission = transmissions.find(t =>
          compare(t, trimData.transmissionType)
        );
        const driveType = transmission.driveTypes.find(d =>
          compare(d, trimData.driveType)
        );
        const trim = driveType.trims.find(t => compare(t, trimData.trim));
        const engineType = trim.engineTypes.find(e =>
          compare(e, trimData.engineType)
        );
        const engineSize = engineType.engineSizes.find(e =>
          compare(e, trimData.engineSize)
        );
        if (engineSize) {
          return trimData;
        }
      } catch (e) {
        // this means that trimData provided is wrong
        return null;
      }
    }
    return null;
  }
);

export const getFirstTrim = createSelector(
  getTrimsData,
  propsSelector,
  ({ transmissions }, selectedTrim) => {
    let transmission;
    let driveType;
    let trim;
    if (transmissions && selectedTrim) {
      for (transmission of transmissions) {
        // just transmissions can have items without sub-arrays
        if (transmission.driveTypes) {
          for (driveType of transmission.driveTypes) {
            for (trim of driveType.trims) {
              if (trim.value.toUpperCase() === selectedTrim.toUpperCase()) {
                return {
                  transmissionType: transmission.value,
                  driveType: driveType.value,
                  trim: trim.value,
                  engineType: trim.engineTypes[0].value,
                  engineSize: trim.engineTypes[0].engineSizes[0].value
                };
              }
            }
          }
        }
      }
    }
    return null;
  }
);

export const transmissions = createSelector(
  getTrimsData,
  ({ transmissions }) => {
    if (transmissions) {
      return transmissions.map(transmission => {
        return { data: transmission.value, label: transmission.label };
      });
    } else {
      return [];
    }
  }
);

export const driveTypes = createSelector(
  getTrimsData,
  getTransmission,
  ({ transmissions: transmissionsData }, selectedTransmission) => {
    if (transmissionsData && selectedTransmission) {
      return transmissionsData
        .find(transmission => transmission.value === selectedTransmission)
        .driveTypes.map(driveType => {
          return { data: driveType.value, label: driveType.label };
        });
    } else {
      return [];
    }
  }
);

export const trims = createSelector(
  getTrimsData,
  getTransmission,
  getDriveType,
  (
    { transmissions: transmissionsData },
    selectedTransmission,
    selectedDriveType
  ) => {
    if (transmissionsData && selectedTransmission && selectedDriveType) {
      return transmissionsData
        .find(transmission => transmission.value === selectedTransmission)
        .driveTypes.find(driveType => driveType.value === selectedDriveType)
        .trims.map(trim => {
          return { data: trim.value, label: trim.label };
        });
    } else {
      return [];
    }
  }
);

export const engineTypes = createSelector(
  getTrimsData,
  getTransmission,
  getDriveType,
  getTrim,
  getTrimsSelectedPosition,
  (
    { transmissions: transmissionsData },
    selectedTransmission,
    selectedDriveType,
    trimSelected,
    trimsSelectedPosition
  ) => {
    if (
      transmissionsData &&
      selectedTransmission &&
      selectedDriveType &&
      trimSelected
    ) {
      return transmissionsData
        .find(transmission => transmission.value === selectedTransmission)
        .driveTypes.find(driveType => driveType.value === selectedDriveType)
        .trims.find((trim, position) =>
          findTrimsCallBack(trim, position, trimSelected, trimsSelectedPosition)
        )
        .engineTypes.map(engineType => {
          return { data: engineType.value, label: engineType.label };
        });
    } else {
      return [];
    }
  }
);

export const engineSizes = createSelector(
  getTrimsData,
  getTransmission,
  getDriveType,
  getTrim,
  getEngineType,
  getTrimsSelectedPosition,
  (
    { transmissions: transmissionsData },
    selectedTransmission,
    selectedDriveType,
    trimSelected,
    engineTypeSelected,
    trimsSelectedPosition
  ) => {
    if (
      transmissionsData &&
      selectedTransmission &&
      selectedDriveType &&
      trimSelected &&
      engineTypeSelected
    ) {
      return transmissionsData
        .find(transmission => transmission.value === selectedTransmission)
        .driveTypes.find(driveType => driveType.value === selectedDriveType)
        .trims.find((trim, position) =>
          findTrimsCallBack(trim, position, trimSelected, trimsSelectedPosition)
        )
        .engineTypes.find(engineType => engineType.value === engineTypeSelected)
        .engineSizes.map(engineSize => {
          return { data: engineSize.value, label: engineSize.label };
        });
    } else {
      return [];
    }
  }
);

export const drivingConditions = createSelector(
  getTrimsData,
  getTransmission,
  getDriveType,
  getTrim,
  getEngineType,
  getEngineSize,
  getTrimsSelectedPosition,
  (
    { transmissions: transmissionsData },
    selectedTransmission,
    selectedDriveType,
    trimSelected,
    engineTypeSelected,
    engineSizeSelected,
    trimsSelectedPosition
  ) => {
    if (
      transmissionsData &&
      selectedTransmission &&
      selectedDriveType &&
      trimSelected &&
      engineTypeSelected &&
      engineSizeSelected
    ) {
      return transmissionsData
        .find(transmission => transmission.value === selectedTransmission)
        .driveTypes.find(driveType => driveType.value === selectedDriveType)
        .trims.find((trim, position) =>
          findTrimsCallBack(trim, position, trimSelected, trimsSelectedPosition)
        )
        .engineTypes.find(engineType => engineType.value === engineTypeSelected)
        .engineSizes.find(engineSize => engineSize.value === engineSizeSelected)
        .drivingConditions.map(dvCondition => {
          return { data: dvCondition.data, label: dvCondition.label };
        });
    } else {
      return [];
    }
  }
);

const findTrimsCallBack = (
  { value: trim },
  position,
  trimSelected,
  trimsSelectedPosition
) => {
  if (trimsSelectedPosition === null) {
    return trim === trimSelected;
  } else {
    return position === trimsSelectedPosition;
  }
};

export const getDefaultDrivingCondition = createSelector(
  getTrimsData,
  ({ defaultDrivingCondition }) => defaultDrivingCondition
);

export const getDrivingCondition = createSelector(
  getMakeDrivingConditions,
  getDefaultDrivingCondition,
  drivingConditions,
  (makeDrivingCondition, ymmDrivingCondition, drivingConditions) => {
    const defaultDrivingCondition =
      makeDrivingCondition || ymmDrivingCondition || null;
    let drivingCondition = drivingConditions[0];
    if (defaultDrivingCondition) {
      drivingCondition = drivingConditions.find(
        dc => dc.data === defaultDrivingCondition
      );
    }
    if (drivingCondition) {
      return drivingCondition.data;
    } else {
      return null;
    }
  }
);

export const hasIDK = state => true;

export const getShowTrimsLane = createSelector(
  getTrimsData,
  ({ transmissions }) => {
    if (transmissions) {
      for (let i = 0; i < transmissions.length; i++) {
        for (
          let j = 0;
          transmissions[i].driveTypes && j < transmissions[i].driveTypes.length;
          j++
        ) {
          if (transmissions[i].driveTypes[j].length > 1) {
            return true;
          }
          for (
            let k = 0;
            transmissions[i].driveTypes[j].trims &&
            k < transmissions[i].driveTypes[j].trims.length;
            k++
          ) {
            if (transmissions[i].driveTypes[j].trims[k].value !== 'UNKNOWN') {
              return true;
            }
          }
        }
      }
    }
    return false;
  }
);

export const hasIDKLabel = createSelector(
  getTrimsData,
  getTransmission,
  ({ transmissions }) => {
    if (transmissions) {
      let hasIDK = false;
      transmissions.forEach(({ value }) => {
        if (value === '') {
          hasIDK = true;
        }
      });
      return hasIDK;
    }
    return null;
  }
);



// WEBPACK FOOTER //
// ./src/store/reducers/trims/selectors.js